import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fmt, LIVE_PLATFORMS, type LivePlatform } from '../api';

const EMOTIONS = ['curiosity', 'desire', 'relief', 'pride', 'fear', 'belonging'] as const;

export default function CampaignLauncher() {
  const nav = useNavigate();
  const [products, setProducts] = useState<any[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [budget, setBudget] = useState(5000);
  const [totalBudget, setTotalBudget] = useState(35_000);
  const [channels, setChannels] = useState<LivePlatform[]>(['meta']);
  const [availableChannels, setAvailableChannels] = useState<LivePlatform[]>(['meta']);
  const [approved, setApproved] = useState(false);
  const [emotion, setEmotion] = useState<string>('curiosity');
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.products().then((rows) => {
      setProducts(rows);
      if (rows.length) setProductId(rows[0].id);
    });
  }, []);

  useEffect(() => {
    api.platformCapabilities().then((capabilities) => {
      const available = LIVE_PLATFORMS.filter((platform) => {
        const capability = capabilities[platform];
        return capability?.enabled && capability.configured;
      });
      setAvailableChannels(available);
      setChannels((current) => current.filter((platform) => available.includes(platform)));
    }).catch(() => {
      setAvailableChannels(['meta']);
      setChannels((current) => current.filter((platform) => platform === 'meta'));
    });
  }, []);

  function toggleChannel(c: LivePlatform) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }

  async function launch() {
    setErr(null);
    setLaunching(true);
    try {
      const r = await api.oneClickLaunch({
        productId,
        dailyBudgetCents: budget,
        totalBudgetCents: totalBudget,
        merchantApproved: true,
        channels,
        emotion,
      });
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLaunching(false);
    }
  }

  const selectedProduct = products.find((p) => p.id === productId);

  if (result) {
    return (
      <>
        <h2 className="page-title">Campaign created for final review</h2>
        <p className="page-sub">
          Platform objects were created paused across {result.channels.length} platform(s). No spend starts until you resume the campaign.
        </p>
        <div className="card card-padded" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>campaign_id</div>
          <code style={{ fontSize: 13 }}>{result.campaignId}</code>
          <div style={{ marginTop: 16 }}>
            <b>{result.creativesCreated} creatives</b> generated, <b>{result.totalAdsCreated} ads</b> deployed.
          </div>
        </div>
        {result.channels.map((c: any) => (
          <div key={c.platform} className="card card-padded" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`badge badge-${c.platform}`}>{c.platform}</span>
              <b>{c.status}</b>
              {c.externalCampaignId && (
                <code style={{ fontSize: 12, color: 'var(--text-mute)' }}>
                  {c.externalCampaignId}
                </code>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 13, color: 'var(--text-sub)' }}>
                {c.creativesCreated} creatives
              </span>
            </div>
            {c.policyFlags?.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warning)' }}>
                ⚠ Policy flags: {c.policyFlags.join(', ')}
              </div>
            )}
            {c.error && (
              <div style={{ marginTop: 8, color: 'var(--danger)', fontSize: 12 }}>
                Error: {c.error}
              </div>
            )}
          </div>
        ))}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => nav(`/campaigns/${result.campaignId}`)}>
            View campaign →
          </button>
          <button className="btn" onClick={() => setResult(null)}>
            Create another
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className="page-title">Create a paused campaign</h2>
      <p className="page-sub">Generate platform-ready campaigns with hard spend limits, then review before activation.</p>

      <div className="card card-padded" style={{ maxWidth: 720 }}>
        <div className="field">
          <label>1. Pick a product</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} — {fmt.money(p.price_cents)} · ad-fit {p.ad_fit_score}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>2. Daily budget — {fmt.money(budget)}</label>
          <input
            type="range"
            min={2000}
            max={20000}
            step={500}
            value={budget}
            onChange={(e) => { const next = Number(e.target.value); setBudget(next); setTotalBudget((current) => Math.max(current, next)); }}
            className="slider"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-mute)' }}>
            <span>$20</span>
            <span>$200</span>
          </div>
        </div>

        <div className="field">
          <label>3. Total spend limit — {fmt.money(totalBudget)}</label>
          <input type="range" min={budget} max={100000} step={1000} value={totalBudget} onChange={(e) => setTotalBudget(Number(e.target.value))} className="slider" />
          <em className="hint">The campaign cannot be created without this merchant-approved lifetime ceiling.</em>
        </div>

        <div className="field">
          <label>4. Channels</label>
          <div className="checkbox-group">
            {availableChannels.map((c) => (
              <label key={c} className={channels.includes(c) ? 'on' : ''}>
                <input
                  type="checkbox"
                  checked={channels.includes(c)}
                  onChange={() => toggleChannel(c)}
                  style={{ accentColor: 'var(--brand)' }}
                />
                <span style={{ textTransform: 'capitalize' }}>{c}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label>5. Emotion target (optional)</label>
          <select value={emotion} onChange={(e) => setEmotion(e.target.value)}>
            {EMOTIONS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>

        <div className="launch-preview">
          We'll create paused ads across <b>{channels.length}</b> platform(s) for <b>{selectedProduct?.title ?? '—'}</b>.
          Daily limit: <b>{fmt.money(budget)}</b>. Total limit: <b>{fmt.money(totalBudget)}</b>.
        </div>

        <label className="field" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} />
          <span>I approve these budgets and understand the campaign will be created paused for my final review.</span>
        </label>

        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>Error: {err}</div>
        )}

        <button
          className="btn btn-primary"
          onClick={launch}
          disabled={!productId || channels.length === 0 || launching || !approved}
        >
          {launching ? 'Creating…' : 'Create paused campaign'}
        </button>
      </div>
    </>
  );
}
